import moment from 'moment';
import i18n from 'i18next';
import { formatDuration } from 'utils/Helpers';
import { serviceName } from 'utils/Reserbus';
import { camelize } from 'humps';
import { generateGoogleMapsSearchUrl } from 'utils/urls';
import amenitiesMap from '../../images/indexAmenities';

/**
 * @typedef {Object} StopRaw
 * @property {string} terminal example: "Terminal de Autobuses" | "Morelia, Terminal de Autobuses"
 * @property {string} departure example: "2018-12-03 17:28:35 -0600"
 * @property {string} arrival example: "2018-12-03 17:48:35 -0600"
 * @property {string} [cityName] example: "Morelia"
 */

/**
 * @typedef {Object} pathSegmentRaw
 * @property {number} schedule example: 1234
 * @property {string} provider example: "SCP"
 * @property {string} origin example: "MONTERREY, MX"
 * @property {string} destination example: "LAREDO, MX"
 * @property {string} departure example: "2018-12-03 17:08:35 -0600"
 * @property {string} arrival example: "2018-12-03 17:58:35 -0600"
 * @property {StopRaw[]} stops
 */

const dateFormat = 'DD MMM YY';
const hourFormat = 'LT';

/** Parse the stops of a trip
 * @param {StopRaw[]} stops
 */
const parseStops = (stops) =>
  stops.map(({ address, arrival, departure, terminal, cityName, permanency, distance }) => {
    const time = [];
    let durationCopy;

    if (arrival && departure) {
      const departureDate = moment(departure);
      const arrivalDate = moment(arrival);
      const duration = moment.duration(departureDate.diff(arrivalDate));
      const nextDay = arrivalDate.isAfter(departureDate, 'day');
      const durationAsMinutes = formatDuration(duration.asMinutes(), 'm');
      durationCopy = durationAsMinutes > 0 ? `de ${formatDuration(durationAsMinutes, 'm')}` : false;

      time.push({
        title: i18n.t('trips:arrives'),
        time: arrivalDate.format(hourFormat),
        nextDayBadge: false,
      });

      time.push({
        title: i18n.t('trips:leaves'),
        time: departureDate.format(hourFormat),
        nextDayBadge: nextDay,
      });
    }

    /** Splits terminal name when information its duplicated.
     * in some tenants api response's terminal comes as "terminal: "León, Central de autobuses"
     * @param {string} terminal
     */
    const splitTerminal = (terminal) => {
      if (terminal.includes(',')) {
        const [part1, part2] = terminal.split(',').map((s) => s.trim());
        return { part1, part2 };
      }
      return { part1: terminal, part2: null };
    };

    const formatedTerminal = splitTerminal(terminal);

    return {
      type: 'stop',
      // Fallback to terminal if cityName is not provided, case of use Brasilia
      title: cityName || formatedTerminal.part1,
      description: durationCopy
        ? i18n.t('trips:time_stopover_in', {
            time: durationCopy,
            place: formatedTerminal.part2 ?? terminal,
          })
        : i18n.t('trips:stopover_in', { place: formatedTerminal.part2 ?? terminal }) +
          (address ? ` - ${address}` : ''),
      time,
      permanency,
      distance,
    };
  });

/**
 * Parse the path of a trip
 * @param {pathSegmentRaw[]} path
 */
function parsePath(path, originName, destinationName, originCityName, destinationCityName, brand) {
  return path.reduce((timeLine, segment, index) => {
    if (index > 0) {
      const { schedule } = path[index - 1];
      const transferCopy = `Cambiar de autobús a ${schedule}`;
      timeLine.push({
        type: 'transfer',
        description: transferCopy,
      });
    }

    const departureCopy = moment(segment.departure).format(hourFormat);
    timeLine.push({
      type: index === 0 ? 'departure' : 'place',
      time: departureCopy,
      title: originCityName,
      description: i18n.t('trips:leaves_from', { place: originName ?? segment.origin }),
    });

    timeLine = [...timeLine, ...parseStops(segment.stops, brand)];

    const arrivalCopy = moment(segment.arrival).format(hourFormat);
    timeLine.push({
      type: index === path.length - 1 ? 'end' : 'place',
      time: arrivalCopy,
      title: destinationCityName,
      description: i18n.t('trips:arrives_in', {
        place: destinationName ?? segment.destination,
      }),
    });

    return timeLine;
  }, []);
}

/**
 * Parse the amenities of a trip
 * @param {Object} params - object with params
 * @param {string} params.lineId - line id
 * @param {*} lines - lines object
 * @returns {Object} - amenities of the trip
 */
const parseAmenities = ({ lineId }, lines) => {
  const lineDetails = lines[camelize(lineId)];

  if (lineDetails) {
    const { logoUrl, serviceType, services } = lineDetails;

    return {
      logo: logoUrl,
      service: serviceName(serviceType),
      amenities: services,
    };
  }

  return {};
};

/**
 * Cleans address string by removing carriage returns \r and newlines \n
 * @param {string} address - The address string to clean
 * @returns {string} Cleaned address with carriage returns and newlines replaced by spaces
 */
const cleanAddressString = (address) => {
  if (!address) return '';
  return address.replace(/\\r\\n|\\r|\\n/g, '\n');
};

/**
 * Parse de details of a trip
 * @param {Object} params - object with params
 * @param {*} params.trip - trip object
 * @param {*} params.lines - lines object
 * @param {*} params.terminals - terminals object
 * @returns {Object} - trip details
 */
export default function parseTripDetails({ trip, lines, terminals }) {
  const {
    departure,
    arrival,
    path,
    destinationAddress,
    destinationId,
    originAddress,
    originId,
    stops,
    lineId,
  } = trip;

  const departureDate = moment(departure);
  const arrivalDate = moment(arrival);
  const duration = moment.duration(arrivalDate.diff(departureDate));
  const departureTime = moment(departure).format('LT');
  const arrivalTime = moment(arrival).format('LT');

  const originName = terminals?.[camelize(originId)].name;
  const originCityName = terminals?.[camelize(originId)].cityName;
  const destinationName = terminals?.[camelize(destinationId)].name;
  const destinationCityName = terminals?.[camelize(destinationId)].cityName;

  const details = {
    originAddress: cleanAddressString(originAddress),
    destinationAddress: cleanAddressString(destinationAddress),
    originCoordinates: terminals?.[camelize(originId)].coordinates,
    destinationCoordinates: terminals?.[camelize(destinationId)].coordinates,
    originName,
    originCityName,
    destinationName,
    destinationCityName,
    departureDate: departureDate.format(dateFormat),
    arrivalDate: arrivalDate.format(dateFormat),
    departureTime,
    arrivalTime,
    tripDuration: formatDuration(duration.asMinutes(), 'm'),
    route: path
      ? parsePath(path, originName, destinationName, originCityName, destinationCityName)
      : null,
    footer: parseAmenities(trip, lines),
    lineDetails: lines[camelize(lineId)],
    stops,
  };

  return details;
}

/**
 *  Check if the hour is early morning
 */
export const earlyMorningEval = (hour) => hour >= 0 && hour < 7;
/**
 * Check if the hour is morning
 */
export const morningEval = (hour) => hour >= 7 && hour < 12;
/**
 * Check if the hour is afternoon
 */
export const afternoonEval = (hour) => hour >= 12 && hour < 19;
/**
 * Check if the hour is night
 */
export const nightEval = (hour) => hour >= 19 && hour < 24;

/**
 * Returns the day time that matches with the departure time range.
 * @param {string} departureTime - trip departure time.
 * @returns {string} Day time that match with the departure time range.
 */
export const getDayTime = (departureTime) => {
  if (earlyMorningEval(departureTime)) return 'earlymorning';
  if (morningEval(departureTime)) return 'morning';
  if (afternoonEval(departureTime)) return 'afternoon';
  return 'night';
};

/**
 * Get the terminal information of a trip.
 * @param {Object} params - object with params
 * @param {Object} params.originTerminal - origin terminal
 * @param {Object} params.destinationTerminal - destination terminal
 * @returns {Object} - terminal information of the trip
 */
export const getTripTerminalInfo = ({ originTerminal, destinationTerminal }) => {
  // Terminal info can be undefined when changing destinations from results page.
  if (!originTerminal || !destinationTerminal) return {};
  return {
    originTerminal: originTerminal.name,
    destinationTerminal: destinationTerminal.name,
    originCity: originTerminal.cityName,
    destinationCity: destinationTerminal.cityName,
    originCoords: originTerminal.coordinates,
    destinationCoords: destinationTerminal.coordinates,
    originMapUrl:
      originTerminal.coordinates &&
      generateGoogleMapsSearchUrl({
        lat: originTerminal.coordinates.lat,
        long: originTerminal.coordinates.long,
      }),
    destinationMapUrl:
      destinationTerminal.coordinates &&
      generateGoogleMapsSearchUrl({
        lat: destinationTerminal.coordinates.lat,
        long: destinationTerminal.coordinates.long,
      }),
    hasCoords: Boolean(
      originTerminal.coordinates?.lat &&
        originTerminal.coordinates.long &&
        destinationTerminal.coordinates?.lat &&
        destinationTerminal.coordinates.long,
    ),
  };
};

/**
 * Normalizes the amenities format to be consistent
 * @param {Array} amenities - Array of amenities that can be strings or objects
 * @returns {Array<string>} Normalized array of unique amenities
 */
export const normalizeAmenities = (amenities) => {
  if (!Array.isArray(amenities)) return [];

  /**
   * Normalizes the icon name using a predefined map
   * @param {string} icon - Icon name to normalize
   * @returns {string} Normalized icon name
   */
  const normalizeIcon = (icon) => {
    const iconMap = {
      ac_unit: 'air_conditioning',
      wc: 'bathroom',
      power: 'power_outlets',
    };

    return iconMap[icon.toLowerCase()] || icon.toLowerCase();
  };

  /**
   * Searches for an amenity in the icons map
   * @param {string} normalizedIcon - Normalized icon to search for
   * @returns {string|undefined} Amenity identifier or undefined if not found
   */
  const findAmenityInMap = (normalizedIcon) =>
    Object.keys(amenitiesMap).find(
      (key) => amenitiesMap[key].toLowerCase() === normalizedIcon || key === normalizedIcon,
    );

  return [
    ...new Set(
      amenities
        .map((amenity) => {
          // Case 1: If it's a string, we just normalize the icon
          if (typeof amenity === 'string') {
            return normalizeIcon(amenity);
          }

          // Case 2: If it's an object with icon, try to find its equivalent
          if (amenity?.icon) {
            const normalizedIcon = normalizeIcon(amenity.icon);
            return (
              findAmenityInMap(normalizedIcon) ||
              amenity.title || // If not found, use title as fallback
              '' // If no title, return empty string
            );
          }

          return '';
        })
        .filter(Boolean),
    ),
  ];
};
